import { useQuery } from "@tanstack/react-query";
import { EventCard } from "@/components/EventCard";
import { supabase } from "@/integrations/supabase/client";
import { Event } from "@/lib/types";
import { Skeleton } from "@/components/ui/skeleton";
import { useCallback, memo } from "react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
const PastEvents = memo(() => {
  const fetchPastEvents = useCallback(async () => {
    try {
      console.log("Fetching past events...");

      // Calculate the cutoff time (current time minus 4 hours)
      const cutoffTime = new Date();
      cutoffTime.setHours(cutoffTime.getHours() - 4);
      const cutoffTimeISO = cutoffTime.toISOString();
      const {
        data,
        error
      } = await supabase.from("events").select("*").eq("status", "published") // Only fetch published events
      .lt("date", cutoffTimeISO) // Only fetch events that have passed by more than 2 hours
      .order("date", {
        ascending: false
      }) // Show most recent past events first
      .limit(20); // Show more past events

      if (error) {
        console.error("Error fetching past events:", error);
        throw error;
      }
      console.log("Fetched past events:", data);
      return data as Event[];
    } catch (error) {
      console.error("Failed to fetch past events:", error);
      toast.error("Failed to load past events. Please try again later.");
      throw error;
    }
  }, []);
  const {
    data: events,
    isLoading,
    error
  } = useQuery({
    queryKey: ["past-events"],
    queryFn: fetchPastEvents,
    staleTime: 1000 * 60 * 5,
    // 5 minutes
    retry: 3
  });
  if (error) {
    console.error("Error in PastEvents:", error);
    return <div className="min-h-screen bg-black text-white">
        <Header />
        <div className="container mx-auto px-4 py-20">
          <div className="text-center text-red-500 p-4 rounded-lg bg-red-500/10">
            Unable to load past events. Please try again later.
          </div>
        </div>
        <Footer />
      </div>;
  }
  return <div className="min-h-screen bg-black text-white">
      <Header />
      <div className="container mx-auto px-4 py-20">
        <div className="text-center mb-12 my-[40px]">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Past Events</h1>
          <p className="text-xl text-gray-300">
            Check out the amazing events that have already happened
          </p>
        </div>
        
        {isLoading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array(9).fill(null).map((_, i) => <div key={i} className="space-y-3">
                  <Skeleton className="h-[200px] w-full bg-gray-800" />
                  <Skeleton className="h-4 w-3/4 bg-gray-800" />
                  <Skeleton className="h-4 w-1/2 bg-gray-800" />
                </div>)}
          </div> : events && events.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map(event => <EventCard key={event.id} event={event} isPastEvent={true} />)}
          </div> : <div className="text-center py-12">
            <p className="text-xl text-gray-400">No past events found.</p>
          </div>}
      </div>
      <Footer />
    </div>;
});
PastEvents.displayName = "PastEvents";
export default PastEvents;